import Fuse from 'fuse.js';
import { DateTime } from 'luxon';
import * as React from 'react';
import Select from 'react-select';
import "react-widgets/styles.css";
import { Collapse, Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { useDebouncedCallback } from 'use-debounce';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { getAllAddresses, selectLocations } from '../../redux/AddressSlice';
import { getAllEntities, getReportingUnitsHierarchy, selectEntities } from '../../redux/EntitiesSlice';
import { Affiliation, AffiliationInput, EditEntityQuery, Entity, EntityInput, EntityReference, createEntity, loadEntity } from '../../redux/EntitySlice';
import { ActiveTaxonomyVersionInfo, TaxonomyTypeEnum, VersionRef, selectCurrentVersion } from '../../redux/TaxonomyVersionSlice';
import utilityHelper from '../../utils/utilityHelper';
import AddressEditor from './AddressEditor';
import DistrictTypeSelector from './DistrictTypeSelector';
import EntityStatusEditor from './EntityStatusEditor';
import FiscalYearEditor from './FiscalYearEditor';


const EntityCreator = (props : any) => {

    const dispatch = useAppDispatch();

    // selectors: selectors should be first in case any hooks or constants depend on their value
    const locationSelector = useAppSelector(selectLocations);
    const entitiesSelector = useAppSelector(selectEntities); 
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);

    //constants
    const defaultValue: { value: string; label: string; } = { value: '', label: '' };
    const primaryOptions = getEntityOptions();
    const entityTypeCodeOptions: Array<{ value: string; label: string; }> = [
        defaultValue,
        { value: 'CITY', label: 'City' },
        { value: 'COUNTY', label: 'County' },
        { value: 'SPECIAL_DISTRICT', label: 'Special District' },
        { value: 'OTHER_ENTITY', label: 'Other Entity' },
    ];
    const districtDependencyOptions: Array<{ value: string; label: string; }> = [
        defaultValue,
        { value: 'INDEPENDENT', label: 'Independent' },
        { value: 'DEPENDENT', label: 'Dependent' },

    ];

    //local hooks
    const [entityName, setEntityName] = React.useState<string>('');
    const [selectedEntityTypeCodeOption, setSelectedEntityTypeCodeOption]
        = React.useState<{ value: string; label: string; }>(defaultValue);
    const [createDate, setCreateDate] = React.useState<string | undefined>(undefined);
    const [selectedPrimaryOption, setSelectedPrimaryOption] = React.useState<{ value: string; label: string; }>(defaultValue);
    const [selectedDistrictTypeOption, setSelectedDistrictTypeOption]
        = React.useState<{ value: string; label: string; }>(defaultValue);

    
    //fiscal due date hooks
    const [fiscalDay, setFiscalDay] = React.useState<number | null>(null);
    const [fiscalMonth, setFiscalMonth] = React.useState<number | null>(null);

    //entity status hooks
    const [dissolvedDate, setDissolvedDate] = React.useState<string | null>(null);
    const [entityStatus, setEntityStatus] = React.useState<string | null>(null);
    const [inactiveDate, setInactiveDate] = React.useState<string | null>(null);
    const [statusReason, setStatusReason] = React.useState<string | null>(null);
    const [renamedDate, setRenamedDate] = React.useState<string | null>(null);
    const [mergedDate, setMergedDate] = React.useState<string | null>(null);

    //address hooks
    const [street, setStreet] = React.useState<string | null>(null);
    const [zipCode, setZipCode] = React.useState<string | null>(null);
    const [pobox, setPobox] = React.useState<string | null>(null);
    const [state, setState] = React.useState<string | null>(null);
    const [county, setCounty] = React.useState<string | null>(null);
    const [cityCode, setCityCode] = React.useState<string | null>(null);


    //UI hooks
    const [readyForUse, setReadyForUse] = React.useState<boolean>(false);

    const [isEntityNameSuggestOpen, setIsEntityNameSuggestOpen] = React.useState<boolean>(false);
    const closeSuggestionDebouce = useDebouncedCallback(() => {
        setIsEntityNameSuggestOpen(false);
    }, 3000);

    const [existingEntityOptions, setExistingEntityOptions] = React.useState<[{ name: string; }]>();
    const [searchResults, setSearchResults] = React.useState(existingEntityOptions);

    const [districtTypeIsValid, setDistrictTypeIsValid] = React.useState<boolean>(true);
    const [districtDependencyStatus, setDistrictDependencyStatus] = React.useState<string>('INDEPENDENT');

    //functions

    function getEntityOptions(): Array<{ value: string; label: string; }> {
        let result: Array<{ value: string; label: string; }> = new Array<{ value: string; label: string; }>();
        result.push(defaultValue);
        entitiesSelector.forEach((ent: any) => {
            result.push({ value: ent.code, label: ent.code + " - " + ent.name });
        })
        return result;
    }

    function getAffiliation(): AffiliationInput | null {
        if (selectedPrimaryOption.value != defaultValue.value) {
            var allEntities = entitiesSelector as EntityReference[]
            var primaryGov = allEntities.find((ent) => ent.code == selectedPrimaryOption.value);
            if (primaryGov) {
                let newAffiliation: AffiliationInput = {
                    affiliate: primaryGov,
                    affiliateId: primaryGov.code,
                    effectiveDate: DateTime.now().toISO(),
                    endDate: null
                }
                return newAffiliation;
            }
            else {
                return null;
            }
        }
        else {
            return null;
        }
    }
    const handleDepStatusChange = function (event: any) {
        setDistrictDependencyStatus(event.target.value);
    }


    async function handleSave() {
        if (IsValidForm()) {
            var convertedDissolvedDate;
            if (dissolvedDate && dissolvedDate.length > 0) {
                convertedDissolvedDate = DateTime.fromISO(dissolvedDate);
            }
            else {
                convertedDissolvedDate = null;
            }

            var convertedInactiveDate;
            if (inactiveDate && inactiveDate.length > 0) {
                convertedInactiveDate = DateTime.fromISO(inactiveDate);
            }
            else {
                convertedInactiveDate = null;
            }
            var districtDependency: string | null = null;
            switch (selectedEntityTypeCodeOption.value) {
                case "SPECIAL_DISTRICT":
                case "OTHER_ENTITY":
                    districtDependency = districtDependencyStatus;
                    break;
            }

            var convertedRenamedDate;
            if (renamedDate && renamedDate.length > 0) {
                convertedRenamedDate = DateTime.fromISO(renamedDate);
            }
            else {
                convertedRenamedDate = null;
            }

            var convertedMergedDate;
            if (mergedDate && mergedDate.length > 0) {
                convertedMergedDate = DateTime.fromISO(mergedDate);
            }
            else {
                convertedMergedDate = null;
            }

            let newEntity: EntityInput = {
                entityId: '0',
                name: entityName,
                nameChangeDate: null,
                dissolvedDate: convertedDissolvedDate ? convertedDissolvedDate : null,
                districttype: selectedDistrictTypeOption.value,
                createDate: DateTime.fromISO(createDate),
                fiscalEndMonth: fiscalMonth ? fiscalMonth : 0,
                fiscalEndDay: fiscalDay ? fiscalDay : 0,
                address: {
                    cityId: cityCode ? cityCode : '',
                    street: street ? street : '',
                    pobox: pobox ? pobox : '',
                    zip: zipCode ? zipCode : ''
                },
                inactiveDate: convertedInactiveDate ? convertedInactiveDate : null,
                renamedDate: convertedRenamedDate ? convertedRenamedDate : null,
                mergedDate: convertedMergedDate ? convertedMergedDate : null,
                status: entityStatus ? entityStatus : '',
                statusReason: statusReason && statusReason.length > 0 ? statusReason : null,
                affiliation: getAffiliation(),
                typecode: selectedEntityTypeCodeOption.value,
                taxonomyYear: versionInfo ? versionInfo.year : null,
                entityReportAccessChangeInput: null,
                doingBusinessAs: null,
                districtDependency: districtDependency,
                districtDependencyChangeDate: null,
                servesInCounties: null
            };

            const originalPromiseResult = await dispatch(createEntity(newEntity)).unwrap()

            if (originalPromiseResult) {
                await dispatch(getAllEntities());
                var eQuery: EditEntityQuery = {
                    entityId: originalPromiseResult.code,
                    taxonomyYear: versionInfo ? versionInfo.year : -1
                };
                await dispatch(loadEntity(eQuery));
                props.setActiveTab("2");
                ResetHooks();
            }         
        }
    }

    const IsValidForm = () => {
        var result = false;
        if (utilityHelper.isTextPresent(entityName)
            && (selectedEntityTypeCodeOption.value != defaultValue.value)
            && (entityStatus != "0")
            && districtTypeIsValid
            && IsPrimaryGovernmentValid()
            && DateTime.fromISO(createDate).isValid
            && IsFiscalYearIsValid() == true) {

            result = true;
        }
        return result;
    }

    const ResetHooks = () => {
        setEntityName('');
        setSelectedEntityTypeCodeOption(defaultValue);
        setDissolvedDate(null);
        setEntityStatus(null);
        setStatusReason("");
        setInactiveDate(null);
        setRenamedDate(null);
        setMergedDate(null);
        setSelectedDistrictTypeOption(defaultValue);
        setSelectedPrimaryOption(defaultValue);
        setDistrictDependencyStatus("INDEPENDENT");
        setStreet(null);
        setPobox(null);
        setCityCode(null);
        setCounty(null);
        setState(null);
        setZipCode(null);
        setCreateDate('');
        setFiscalDay(null);
        setFiscalMonth(null);
    }

    

    const IsPrimaryGovernmentValid = (): boolean => {

        var result = false;

        if (selectedEntityTypeCodeOption.value == 'SPECIAL_DISTRICT'
            || selectedEntityTypeCodeOption.value == 'OTHER_ENTITY') {

            if (selectedPrimaryOption.value != defaultValue.value) {
                result = true;
            }
            else {
                result = true;
            }

        }
        else {
            result = true;
        }


        return result;
    }

    const IsFiscalYearIsValid = (): boolean => {
        var result = false;

        if (fiscalMonth && fiscalDay) {
            if (fiscalMonth != 0 && fiscalDay != 0) {
                result = true;
            }
        }
        return result;
    }

    const searchEntityNames = (inputName: string) => {
        setIsEntityNameSuggestOpen(true);

        const fuse = new Fuse(existingEntityOptions, {
            keys: ["name"],
            shouldSort: true,
            threshold: .3,
            ignoreLocation: true,
            //location: 0,
            //distance: 100,
        });

        const result = fuse.search(inputName);
        let finalResult: any = [];
        if (result.length) {
            for (var i = 0; i < result.length; i++) {
                finalResult.push({ name: result[i].item.name});
            }
        }
        setSearchResults(finalResult.sort((a:any,b:any) => a.name.localeCompare(b.name) ));
        setEntityName(inputName);
        closeSuggestionDebouce();
    };

    const suggestionInteraction = () => {
        setIsEntityNameSuggestOpen(true);
        closeSuggestionDebouce();
    };

    function canHavePrimary() : boolean {
        if (selectedEntityTypeCodeOption.value === defaultValue.value) {
            return false;
        }
        var result: boolean = false;
        switch (selectedEntityTypeCodeOption.value) {
            case "SPECIAL_DISTRICT":
            case "OTHER_ENTITY":
                result = true;
                break;
        }
        return result;
    }

    React.useEffect(() => {
        if (locationSelector === null ||
            (locationSelector !== null && locationSelector.length === 0)
        ) {
            dispatch(getAllAddresses());
        }
        if (entitiesSelector == null ||
            (entitiesSelector !== null && entitiesSelector.length === 0)) {
            dispatch(getAllEntities());
        }
    }, []);

    React.useEffect(() => {
        if (versionInfo) {

            let taxonomyType: TaxonomyTypeEnum = TaxonomyTypeEnum.AFR;

            if (versionInfo.type != null) {
                taxonomyType = versionInfo.type;
            }

            let versionReference: VersionRef = {
                type: taxonomyType,
                year: versionInfo.year
            };

            dispatch(getReportingUnitsHierarchy(versionReference));
            setReadyForUse(true);
        }
        else {
            setReadyForUse(false);
        }
    }, [versionInfo])

    
    React.useEffect(() => {

        if (entitiesSelector != null) {
            var fuzzyOptionList = entitiesSelector.map((entityOption: Entity) => ({ name: entityOption.name }));
            setExistingEntityOptions(fuzzyOptionList);
        }

    }, [entitiesSelector])


    return (
        <React.Fragment>
            <Form style={{ marginTop: "2em" }}>
                <Row>
                    <Col md="6">
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Name:</Label>
                                </Col>
                                <Col md="9">
                                    <Input type="text" value={entityName}
                                        onChange={(e) => { searchEntityNames(e.currentTarget.value) }}
                                        valid={utilityHelper.isTextPresent(entityName) && readyForUse}
                                        invalid={!utilityHelper.isTextPresent(entityName) && readyForUse}
                                        disabled={!readyForUse}

                                    />
                                    <FormFeedback>Entity name is required.</FormFeedback>
                                    {
                                        isEntityNameSuggestOpen == true && searchResults && searchResults.length > 0 ?
                                            (<div style={{
                                                height: "10em", overflowY: "scroll", position: "absolute",
                                                backgroundColor: "white",
                                                zIndex: 1
                                            }} onMouseOver={suggestionInteraction} onScroll={ suggestionInteraction }>
                                                <ul style={{ listStyleType: "none" }}>
                                                    {
                                                        searchResults.map((item) => (

                                                            <li >{item.name}</li>

                                                        ))}
                                                </ul>
                                            </div>) : (<></>)
                                    }

                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Entity Type Code:</Label>
                                </Col>
                                <Col md="9">
                                    <Select
                                        value={selectedEntityTypeCodeOption}
                                        onChange={(e: any) => {
                                            setSelectedEntityTypeCodeOption(e);
                                            setSelectedPrimaryOption(defaultValue);
                                            setDistrictDependencyStatus("INDEPENDENT");
                                            setSelectedDistrictTypeOption(defaultValue);
                                        }}
                                        options={entityTypeCodeOptions}
                                        style={{ width: "25em" }}
                                        isDisabled={!readyForUse}
                                    />
                                    <Input type="hidden" value={selectedEntityTypeCodeOption.value}
                                        onChange={(e) => { }}
                                        valid={selectedEntityTypeCodeOption.value != defaultValue.value && readyForUse}
                                        invalid={selectedEntityTypeCodeOption.value == defaultValue.value && readyForUse}
                                        disabled={true}
                                    />
                                    <FormFeedback>Entity type is required.</FormFeedback>
                                </Col>
                                <FormFeedback>Entity type code is required.</FormFeedback>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <EntityStatusEditor
                                        status={entityStatus}
                                        setEntityStatus={setEntityStatus}
                                        statusReason={statusReason}
                                        setStatusReason={setStatusReason}
                                        dissolvedDate={dissolvedDate}
                                        setDissolvedDate={setDissolvedDate}
                                        inactiveDate={inactiveDate}
                                        setInactiveDate={setInactiveDate}
                                        renamedDate={renamedDate}
                                        setRenamedDate={setRenamedDate}
                                        reactivateDate={null}
                                        setReactivateDate={null}
                                        readyForUse={readyForUse}
                                        canReactivate={false}
                                        enableAllEntityStatus={false}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <DistrictTypeSelector
                                selectedEntityTypeCodeOption={selectedEntityTypeCodeOption}
                                selectedDistrictTypeOption={selectedDistrictTypeOption}
                                setSelectedDistrictTypeOption={setSelectedDistrictTypeOption}
                                districtTypeIsValid={districtTypeIsValid}
                                setDistrictTypeIsValid={setDistrictTypeIsValid}
                                isCreateMode={ true }
                            />
                        </FormGroup>
                        <Collapse isOpen={selectedEntityTypeCodeOption.value === 'SPECIAL_DISTRICT' || selectedEntityTypeCodeOption.value === 'OTHER_ENTITY'}>
                            <FormGroup>
                                <Row>
                                    <Col md="4" style={{ textAlign: "left" }}>
                                        <Label>District Dependency:</Label>
                                    </Col>
                                    <Col md="8">
                                        <Row>
                                            <Col>
                                                <Label className="ml-4" check>
                                                    <Input type="radio"
                                                        name="ind"
                                                        value={"INDEPENDENT"}
                                                        onChange={handleDepStatusChange}
                                                        checked={districtDependencyStatus === "INDEPENDENT"}
                                                    /><b>Independent</b>
                                                </Label>
                                            </Col>
                                            <Col>
                                                <Label className="ml-4" check>
                                                    <Input type="radio"
                                                        name="dep"
                                                        value={"DEPENDENT"}
                                                        onChange={handleDepStatusChange}
                                                        checked={districtDependencyStatus === "DEPENDENT"}
                                                    /><b>Dependent</b>
                                                </Label>
                                            </Col>
                                        </Row>
                                        <FormFeedback>District dependency is required for special districts and other entities.</FormFeedback>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Collapse>
                        <Collapse isOpen={canHavePrimary()}>
                            <FormGroup>
                                <Row>
                                    <Col >
                                        <Label>Primary Government Entity:</Label>
                                    </Col>
                                    <Col md="9">
                                        <Select
                                            value={selectedPrimaryOption}
                                            onChange={(e: any) => { setSelectedPrimaryOption(e) }}
                                            options={primaryOptions}
                                            style={{ width: "25em" }}
                                        />

                                    </Col>
                                </Row>
                            </FormGroup>
                        </Collapse>
                       
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Row>
                                <Col>
                                    <AddressEditor
                                        street={street}
                                        setStreet={setStreet}
                                        zipCode={zipCode}
                                        setZipCode={setZipCode}
                                        poBox={pobox}
                                        setPoBox={setPobox}
                                        cityCode={cityCode}
                                        setCityCode={setCityCode}
                                        county={county}
                                        setCounty={setCounty}
                                        state={state}
                                        setState={setState}
                                        canEdit={readyForUse}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col >
                                    <Label>Entity Creation Date:</Label>
                                </Col>
                                <Col md="9">
 
                                    <Input type="date" value={createDate}
                                        onChange={(e) => { setCreateDate(e.currentTarget.value) }}
                                        valid={DateTime.fromISO(createDate).isValid}
                                        invalid={DateTime.fromISO(createDate).isValid == false && readyForUse == true}
                                        disabled={!readyForUse}
                                        max={DateTime.now().toFormat('yyyy-LL-dd')}
                                    />
                                    <FormFeedback>Enter a valid date.</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Row>
                                <Col>
                                    <FiscalYearEditor
                                        fiscalMonth={fiscalMonth}
                                        setFiscalMonth={setFiscalMonth}
                                        fiscalDay={fiscalDay}
                                        setFiscalDay={setFiscalDay}
                                        readyForUse={readyForUse}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
                <Button
                    onClick={handleSave}
                    disabled={!IsValidForm()}
                    color={IsValidForm() == true ? "success" : "secondary" }
                >Create</Button>
            </Form>
        </React.Fragment>
    );

}


export default EntityCreator;